import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { deleteUser } from '../api/admin';
import { User } from '../types/user';

interface DeleteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

const DARK_COLORS = {
  SURFACE: '#121212',
  BORDER: '#333333',
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#b0b0b0',
  ERROR: '#ff4444',
  WARNING: '#ff9800',
};

export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await deleteUser(user.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      let errorMessage = 'Errore durante l\'eliminazione dell\'utente';
      
      // Check for specific error messages from API
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        if (detail.includes('Cannot delete users from root organization')) {
          errorMessage = '❌ Impossibile eliminare utenti dall\'organizzazione root. Gli utenti dell\'organizzazione root non possono essere eliminati.';
        } else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: DARK_COLORS.SURFACE,
          border: `1px solid ${DARK_COLORS.BORDER}`,
        }
      }}
    >
      <DialogTitle sx={{ color: DARK_COLORS.TEXT_PRIMARY }}>
        Conferma Eliminazione
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Warning sx={{ color: DARK_COLORS.WARNING, fontSize: 48, mt: 1 }} />
          <Box>
            <Typography variant="body1" sx={{ color: DARK_COLORS.TEXT_PRIMARY, mb: 2 }}>
              Sei sicuro di voler eliminare questo utente?
            </Typography>
            <Box sx={{ 
              bgcolor: 'rgba(255, 152, 0, 0.1)', 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${DARK_COLORS.WARNING}`
            }}>
              <Typography variant="body2" sx={{ color: DARK_COLORS.TEXT_PRIMARY, fontWeight: 600 }}>
                Username: {user.username}
              </Typography>
              <Typography variant="body2" sx={{ color: DARK_COLORS.TEXT_SECONDARY }}>
                Email: {user.email}
              </Typography>
              {user.full_name && (
                <Typography variant="body2" sx={{ color: DARK_COLORS.TEXT_SECONDARY }}>
                  Nome: {user.full_name}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: DARK_COLORS.WARNING, mt: 2 }}>
              ⚠️ Questa azione non può essere annullata. Tutti i dati associati a questo utente verranno eliminati.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: DARK_COLORS.TEXT_SECONDARY }}>
          Annulla
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Eliminazione...' : 'Elimina Utente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
